'use client';
import { useEffect, useRef, useState, useCallback } from 'react';
import { fetchNflState, fetchLeague, buildBoard, resolveDisplayWeek } from './sleeperAdapter';

/* ============================================================
   useBoardFeed — the poll + diff loop (§5.1).

   One central fetch per app instance (never per-viewer; Sleeper's limit is
   1000 calls/min and a poll is 4 calls). Coarse interval, 45s default.

   What it does beyond "setInterval(fetch)":

   1. DIFF. buildBoard() returns fresh object identities every poll. If we
      handed those straight to the renderer, every SplitFlap would get a new
      `text` string that happens to be equal — harmless (they no-op on an
      unchanged target) but it re-renders the whole tree for nothing. So we
      keep the PREVIOUS board and reuse the previous row object whenever a
      row is value-identical. Unchanged rows keep referential identity, so
      React.memo on MatchupRow short-circuits them entirely.

   2. POLL HISTORY. deriveStatus() takes `prev` (per-matchup snapshots) to
      tell live from in_progress. We accumulate that here and feed it back
      into the next buildBoard call. Capped at HISTORY_MAX per matchup.

   3. WEEK ROLLOVER. When /state/nfl's week changes, we surface it as
      `week`, which the app threads into MatchupRow's spin prop. That — and
      only that — fires the avatar slot-machine. We also clear the history
      on rollover so last week's points can't read as "live" movement.

   4. BACKOFF + VISIBILITY. Failed polls back off exponentially (to 5min)
      instead of hammering. A hidden tab (TV asleep, other window) stops
      polling and refetches immediately on wake.

   Returns:
     { board, week, status, error, lastUpdated, refresh }
   status: 'idle' | 'loading' | 'ready' | 'error'
   ============================================================ */

const DEFAULT_INTERVAL = 45000;
const MAX_BACKOFF = 300000;
const HISTORY_MAX = 4;

// ---- pure diff helpers (exported for testing) ----------------------------

// Value-compare a decorated team. Avatar/name/rank/points are the only
// things the row renders; record is shown in standings, so include it.
export function teamEqual(a, b) {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.rosterId === b.rosterId &&
    a.name === b.name &&
    a.avatar === b.avatar &&
    a.rank === b.rank &&
    // points are rendered rounded — only a whole-point move is a visible flip
    Math.round(a.points ?? 0) === Math.round(b.points ?? 0) &&
    a.record === b.record
  );
}

export function rowEqual(a, b) {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.matchupId === b.matchupId &&
    a.boardStatus === b.boardStatus &&
    // Playoff annotations are rendered cells, so they belong in the diff:
    // without them a row whose label changed would keep its old object and
    // the label would never flip.
    a.playoffLabel === b.playoffLabel &&
    a.isBye === b.isBye &&
    a.isPlayoff === b.isPlayoff &&
    teamEqual(a.teamA, b.teamA) &&
    teamEqual(a.teamB, b.teamB)
  );
}

export function standingEqual(a, b) {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.rosterId === b.rosterId &&
    a.rank === b.rank &&
    a.name === b.name &&
    a.avatar === b.avatar &&
    a.record === b.record &&
    // Playoff annotations are rendered (dimming, CHAMPION, medals), so a
    // change in any of them has to break referential equality or the row
    // keeps its old object and the medal never appears.
    a.eliminated === b.eliminated &&
    a.isChampion === b.isChampion &&
    a.place === b.place &&
    a.isLastPlace === b.isLastPlace &&
    Math.round(a.pointsFor ?? 0) === Math.round(b.pointsFor ?? 0)
  );
}

// Reuse previous objects wherever nothing visible changed, so referential
// equality survives a poll and memoized rows don't re-render.
export function reconcileBoard(prevBoard, nextBoard) {
  if (!prevBoard) return nextBoard;

  // Row identity key. In the regular season matchup_id is fixed for the week
  // and is the right key. In the playoffs a bracket game's matchup_id is not
  // guaranteed stable across weeks, while the bracket's own `m` is unique
  // within the bracket -- so prefer it when we have it.
  const rowKey = (r) => (r.bracketMatchId != null ? `b${r.bracketMatchId}` : `m${r.matchupId}`);

  const prevRows = new Map(prevBoard.rows.map((r) => [rowKey(r), r]));
  let rowsChanged = prevBoard.rows.length !== nextBoard.rows.length;
  const rows = nextBoard.rows.map((r, i) => {
    const p = prevRows.get(rowKey(r));
    if (p && rowEqual(p, r)) {
      if (prevBoard.rows[i] !== p) rowsChanged = true; // row order moved
      return p;
    }
    rowsChanged = true;
    return r;
  });

  const prevStand = new Map(prevBoard.standings.map((s) => [s.rosterId, s]));
  let standChanged = prevBoard.standings.length !== nextBoard.standings.length;
  const standings = nextBoard.standings.map((s, i) => {
    const p = prevStand.get(s.rosterId);
    if (p && standingEqual(p, s)) {
      if (prevBoard.standings[i] !== p) standChanged = true;
      return p;
    }
    standChanged = true;
    return s;
  });

  const metaChanged =
    prevBoard.leagueName !== nextBoard.leagueName ||
    prevBoard.week !== nextBoard.week ||
    prevBoard.inSeason !== nextBoard.inSeason ||
    playoffChanged(prevBoard.playoff, nextBoard.playoff);

  if (!rowsChanged && !standChanged && !metaChanged) return prevBoard;

  return {
    leagueName: nextBoard.leagueName,
    week: nextBoard.week,
    inSeason: nextBoard.inSeason,
    // Playoff meta must survive reconciliation or the round labels vanish
    // on the first poll after load. Reuse the previous object when nothing
    // in it moved, so `playoff` identity is stable for memo boundaries.
    playoff: playoffChanged(prevBoard.playoff, nextBoard.playoff)
      ? nextBoard.playoff
      : prevBoard.playoff,
    playoffWeekStart: nextBoard.playoffWeekStart,
    rows: rowsChanged ? rows : prevBoard.rows,
    standings: standChanged ? standings : prevBoard.standings,
  };
}

// Compare the fields the renderer actually reads. `eliminated` is an array
// that gets rebuilt every poll, so identity comparison would report a change
// on every single tick and defeat the whole reconciliation pass.
export function playoffChanged(a, b) {
  if (a === b) return false;
  if (!a || !b) return true;
  return (
    a.round !== b.round ||
    a.totalRounds !== b.totalRounds ||
    a.roundLabel !== b.roundLabel ||
    a.championRosterId !== b.championRosterId ||
    (a.eliminated || []).join(',') !== (b.eliminated || []).join(',') ||
    (a.byes || []).join(',') !== (b.byes || []).join(',')
  );
}

// Append this poll's points to the per-matchup history deriveStatus() reads.
// Poll history is keyed by the ROSTER PAIR, not the matchup id.
//
// Why: buildBoard reads this history before the playoff annotation runs, so
// it can't know a row's bracket id yet -- but the two rosters in a game are
// known at both ends and don't change. A pair key is therefore the one
// identity that survives the whole pipeline in both season phases.
export function historyKey(row) {
  const a = row.teamA?.rosterId;
  const b = row.teamB?.rosterId;
  if (a == null && b == null) return `m${row.matchupId}`;
  const x = Number(a ?? -1);
  const y = Number(b ?? -1);
  return x < y ? `p${x}:${y}` : `p${y}:${x}`;
}

export function pushHistory(history, rows, t = Date.now()) {
  const out = {};
  for (const r of rows) {
    const key = historyKey(r);
    const prior = history[key] || [];
    const points = [r.teamA?.points ?? 0, r.teamB?.points ?? 0];
    const last = prior[prior.length - 1];
    // don't record a duplicate sample; a flat poll shouldn't age out real history
    if (last && last.points[0] === points[0] && last.points[1] === points[1]) {
      out[key] = prior;
    } else {
      out[key] = [...prior, { points, t }].slice(-HISTORY_MAX);
    }
  }
  return out;
}

// The avatar pool for the slot-machine: every distinct avatar in the league.
export function avatarPool(board) {
  if (!board) return [];
  const seen = new Set();
  for (const s of board.standings) if (s.avatar) seen.add(s.avatar);
  return [...seen];
}

// ---- the hook -------------------------------------------------------------

export default function useBoardFeed(leagueId, {
  interval = DEFAULT_INTERVAL,
  fetchImpl,
  enabled = true,
  // Ambient default: matchup rows stay pinned to their matchup for the whole
  // week. A team climbing the ranks mid-week is real, but reordering the rows
  // under it makes every name and score cell re-target at once -- the board
  // churns and nothing reads as meaningful. Rank movement belongs in the
  // standings view, where the cascade is the payoff. Pass 'rank' to restore
  // rank-led ordering.
  rowOrder = 'fixed',
  // Where the data comes from.
  //   'api'     -> GET {apiBase}/<leagueId>, one cached fetch shared by all
  //                viewers (the deployed default; see api/board/[leagueId].js)
  //   'sleeper' -> hit Sleeper's 4 endpoints straight from the browser.
  //                Fine for local dev, but it multiplies calls by viewer count.
  source = 'api',
  apiBase = '/api/board',
} = {}) {
  const [board, setBoard] = useState(null);
  const [week, setWeek] = useState(null);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const boardRef = useRef(null);
  const historyRef = useRef({});
  const weekRef = useRef(null);
  const failuresRef = useRef(0);
  const timerRef = useRef(null);
  const abortRef = useRef(0); // generation counter; stale polls self-cancel

  const poll = useCallback(async () => {
    const gen = ++abortRef.current;
    const f = fetchImpl || (typeof fetch !== 'undefined' ? fetch : null);
    if (!f) throw new Error('no fetch available');

    let next;
    let wk;
    let inSeason = true;

    if (source === 'api') {
      // The server already ran buildBoard, so the client just consumes rows.
      // Poll history / live-status detection still happens here, because it
      // depends on what THIS viewer saw last -- a browser that just opened
      // the page has no history and correctly shows in_progress, not live.
      const res = await f(`${apiBase}/${leagueId}`);
      if (!res.ok) throw new Error(`Board API ${res.status}`);
      const payload = await res.json();
      wk = payload.week;
      inSeason = payload.inSeason !== false;
      if (weekRef.current !== null && weekRef.current !== wk) {
        historyRef.current = {};
      }
      next = payload;
    } else {
      const nflState = await fetchNflState(f);
      // Off-season: show the last week the league actually played rather
      // than an unplayed week that would return an empty board.
      const resolved = await resolveDisplayWeek(leagueId, nflState, f);
      wk = resolved.week;
      inSeason = resolved.inSeason;

      // week rollover: drop history so stale points can't read as movement
      if (weekRef.current !== null && weekRef.current !== wk) {
        historyRef.current = {};
      }

      const raw = await fetchLeague(leagueId, wk, f);
      next = buildBoard(raw, {
        nflState,
        prevByMatchup: historyRef.current,
        rowOrder,
      });
    }

    if (gen !== abortRef.current) return; // a newer poll already started

    if (next && typeof next === 'object') next.inSeason = inSeason;
    historyRef.current = pushHistory(historyRef.current, next.rows);
    const reconciled = reconcileBoard(boardRef.current, next);
    boardRef.current = reconciled;
    weekRef.current = wk;

    setBoard(reconciled);
    setWeek(wk);
    setLastUpdated(Date.now());
    setStatus('ready');
    setError(null);
    failuresRef.current = 0;
  }, [leagueId, fetchImpl, rowOrder, source, apiBase]);

  const runLoop = useCallback(async () => {
    clearTimeout(timerRef.current);
    try {
      await poll();
    } catch (e) {
      failuresRef.current += 1;
      setError(e);
      // only surface an error state if we have nothing to show; a failed
      // poll over a good board should leave the board on screen
      setStatus(boardRef.current ? 'ready' : 'error');
    }
    const backoff = failuresRef.current
      ? Math.min(interval * 2 ** failuresRef.current, MAX_BACKOFF)
      : interval;
    timerRef.current = setTimeout(runLoop, backoff);
  }, [poll, interval]);

  const refresh = useCallback(() => {
    failuresRef.current = 0;
    runLoop();
  }, [runLoop]);

  useEffect(() => {
    if (!leagueId || !enabled) {
      setStatus('idle');
      return undefined;
    }
    // a new league is a different board entirely
    boardRef.current = null;
    historyRef.current = {};
    weekRef.current = null;
    failuresRef.current = 0;
    setBoard(null);
    setStatus('loading');
    runLoop();

    const onVisible = () => {
      if (typeof document !== 'undefined' && !document.hidden) refresh();
      else clearTimeout(timerRef.current);
    };
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisible);
    }
    return () => {
      abortRef.current += 1;
      clearTimeout(timerRef.current);
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisible);
      }
    };
  }, [leagueId, enabled, runLoop, refresh]);

  return { board, week, status, error, lastUpdated, refresh };
}
